import { useState, useEffect } from "react";
import logoImage from "../../assets/images/logo.png";

function Header() {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
    setIsMenuOpen(false);
  };

  const menuItems = [
    {
      label: "WhitePaper",
      action: () =>
        window.open(
          "https://switch-protocol.gitbook.io/switch-protocol/",
          "_blank"
        ),
    },
    { label: "Developers", action: () => scrollToSection("section2") },
    { label: "Ecosystem", action: () => scrollToSection("section3") },
    { label: "Community", action: () => scrollToSection("section8") },
  ];

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          transform: isVisible ? "translateY(0)" : "translateY(-100%)",
        }}
      >
        <nav className="w-full h-20 flex items-center justify-between px-4 md:px-20 py-[26px] relative">
          <div className="flex items-center gap-2">
            <img
              src={logoImage}
              alt="SWITCH Logo"
              className="w-[120px] md:w-[198px] h-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-12">
            <button
              onClick={() =>
                window.open(
                  "https://switch-protocol.gitbook.io/switch-protocol/",
                  "_blank"
                )
              }
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <span className="text-white text-center text-[17px] leading-[1.6] font-normal">
                WhitePaper
              </span>
            </button>

            <button
              onClick={() => scrollToSection("section2")}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <span className="text-white text-center text-[17px] leading-[1.6] font-normal">
                Developers
              </span>
            </button>

            <button
              onClick={() => scrollToSection("section3")}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <span className="text-white text-center text-[18px] leading-[1.52] font-normal">
                Ecosystem
              </span>
            </button>

            <button
              onClick={() => scrollToSection("section8")}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <span className="text-white text-center text-[17px] leading-[1.6] font-normal">
                Community
              </span>
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            className="md:hidden flex flex-col gap-1.5 w-6 h-6 justify-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-full h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            ></span>
            <span
              className={`block w-full h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            ></span>
            <span
              className={`block w-full h-0.5 bg-white transition-all duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            ></span>
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay - Outside header to cover full screen */}
      <div
        className={`fixed inset-0 bg-black/80 z-[60] md:hidden transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsMenuOpen(false)}
      ></div>

      {/* Mobile Menu Sidebar - Outside header to cover full screen */}
      <div
        className={`fixed top-0 right-0 h-full w-80 z-[70] md:hidden transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          background:
            "linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 20, 30, 0.95) 50%, rgba(0, 0, 0, 0.95) 100%)",
        }}
      >
        <div className="flex flex-col h-full pt-20 px-6 relative">
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-white hover:opacity-80 transition-opacity"
            onClick={() => setIsMenuOpen(false)}
            aria-label="Close menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {menuItems.map((item, index) => (
            <button
              key={index}
              className="flex items-center justify-between py-4 text-white text-left border-b border-white/10 hover:opacity-80 transition-opacity"
              onClick={() => {
                if (item.action) {
                  item.action();
                }
                setIsMenuOpen(false);
              }}
            >
              <span className="text-[17px] font-normal">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export default Header;
